/**
 * Maya — Assistente IA do GridProfile
 * GridProfile / NeuroSynx Ltda
 *
 * Chat flutuante no canto inferior direito, sempre visível em rotas autenticadas.
 * Powered by Claude Sonnet 4.5 via Emergent Universal Key.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkles, Send, X, Loader2, Trash2, MessageCircle, PlusCircle,
  Bot, History, ArrowLeft,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useMaya } from '../contexts/MayaContext';
import { useConfirm } from '../hooks/useConfirm';
import MayaAvatarReactive from './maya/MayaAvatar';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// =============================================================================
// Avatar da Maya — wrapper compatível com a antiga API (size numérico) que
// delega para o novo componente reativo image-based com Framer Motion.
// =============================================================================
const SIZE_TO_PRESET = (px) => {
  if (px <= 28) return 'xs';
  if (px <= 44) return 'sm';
  if (px <= 80) return 'md';
  if (px <= 120) return 'lg';
  return 'xl';
};

const MayaAvatar = ({ size = 40, animated = false, variant }) => (
  <MayaAvatarReactive
    variant={variant || (animated ? 'thinking' : 'happy')}
    size={SIZE_TO_PRESET(size)}
    pulse={animated}
    showStatusDot={size >= 40}
    data-testid="maya-chat-avatar"
  />
);

// =============================================================================
// Bolha de mensagem
// =============================================================================
const ACTION_REGEX = /<ACTION>([\s\S]*?)<\/ACTION>/;

const parseAction = (content) => {
  const m = content.match(ACTION_REGEX);
  if (!m) return { text: content, action: null };
  try {
    const action = JSON.parse(m[1].trim());
    const text = content.replace(ACTION_REGEX, '').trim();
    return { text, action };
  } catch {
    return { text: content, action: null };
  }
};

const dispatchAction = async (result, auth, API) => {
  /** Quando a tool retorna dispatch_url/dispatch_sequence/download_url, executa via axios ou abre uma aba. */
  const axios = (await import('axios')).default;
  if (result?.download_url) {
    // Para download (PDF), abre em nova aba COM o token via fetch + blob (uma vez que o endpoint exige auth)
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}${result.download_url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // revoga após 60s para liberar memória
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      return { downloaded: true };
    } catch (e) {
      throw new Error('Falha ao baixar arquivo: ' + e.message);
    }
  }
  if (result?.dispatch_sequence) {
    const responses = [];
    for (const c of result.dispatch_sequence) {
      const r = await axios({ method: c.method, url: `${API}${c.url}`, data: c.body || {}, ...auth() });
      responses.push(r.data);
    }
    return responses;
  }
  if (result?.dispatch_url) {
    const r = await axios({
      method: result.dispatch_method || 'POST',
      url: `${API}${result.dispatch_url}`,
      data: result.dispatch_body || (result.dispatch_method === 'GET' ? undefined : {}),
      ...auth(),
    });
    return r.data;
  }
  return null;
};

const MessageBubble = ({ msg, onExecutar }) => {
  const isUser = msg.role === 'user';
  const { text, action } = !isUser ? parseAction(msg.content) : { text: msg.content, action: null };
  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-3`}>
      {!isUser && <div className="shrink-0 mt-1"><MayaAvatar size={32} /></div>}
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${isUser ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'}`}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{msg.content}</p>
        ) : (
          <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-code:text-xs prose-code:bg-slate-200 prose-code:px-1 prose-code:rounded">
            <ReactMarkdown>{text}</ReactMarkdown>
            {action && (
              <div className="mt-2 p-2 bg-white border border-indigo-200 rounded-lg" data-testid="maya-action-card">
                <p className="text-xs text-slate-600 mb-2">⚙️ {action.confirmacao}</p>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7"
                  onClick={() => onExecutar(action)} data-testid="maya-execute-btn">
                  ✓ {action.label_botao || 'Executar ação'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Maya Chat — botão flutuante + painel
// =============================================================================
const MayaChat = () => {
  const { open, setOpen, consumePrompt, pendingPrompt } = useMaya();
  const { confirm, ConfirmDialog } = useConfirm();
  const [view, setView] = useState('chat'); // 'chat' | 'history'
  const [sessaoId, setSessaoId] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [input, setInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [carregandoHist, setCarregandoHist] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  const isAuthed = !!localStorage.getItem('token');

  // Carregar sugestões na primeira abertura
  useEffect(() => {
    if (open && sugestoes.length === 0 && isAuthed) {
      axios.get(`${API}/api/assistant/sugestoes`, auth())
        .then(r => setSugestoes(r.data?.sugestoes || []))
        .catch(() => {});
    }
  }, [open, sugestoes.length, isAuthed]);

  // Auto-scroll para o final
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [mensagens, enviando]);

  // Foco no input ao abrir
  useEffect(() => {
    if (open && view === 'chat' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open, view]);

  // Consumir prompt contextual pendente: cria nova sessão e envia automaticamente
  useEffect(() => {
    if (open && pendingPrompt && !enviando) {
      const p = consumePrompt();
      if (p?.text) {
        // limpa estado da conversa anterior e dispara
        setSessaoId(null);
        setMensagens([]);
        setView('chat');
        setTimeout(() => enviar(p.text), 100);
      }
    }
  }, [open, pendingPrompt]); // eslint-disable-line

  const executarAcao = useCallback(async (action) => {
    if (!action?.tool) return;
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/api/assistant/execute`, {
        sessao_id: sessaoId,
        tool: action.tool,
        params: action.params || {},
      }, auth());
      if (r.data?.ok) {
        if (r.data.resultado?.dispatch_url || r.data.resultado?.dispatch_sequence || r.data.resultado?.download_url) {
          try {
            await dispatchAction(r.data.resultado, auth, API);
            toast.success(`✅ ${r.data.mensagem}`);
          } catch (e) {
            toast.error(`⚠️ Falhou: ${e?.response?.data?.detail || e.message}`);
          }
        } else {
          toast.success(`✅ ${r.data.mensagem}`);
        }
        setMensagens((m) => [...m, { id: `act-${Date.now()}`, role: 'assistant', content: `✅ **${r.data.mensagem}**` }]);
      } else {
        toast.error(r.data?.mensagem || 'Falha ao executar');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao executar ação');
    } finally { setEnviando(false); }
  }, [sessaoId]);

  const enviar = useCallback(async (texto) => {
    const msg = (texto ?? input).trim();
    if (!msg || enviando) return;
    setInput('');
    // adiciona msg local imediatamente
    const userMsg = { id: `local-${Date.now()}`, role: 'user', content: msg };
    setMensagens((m) => [...m, userMsg]);
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/api/assistant/chat`, {
        sessao_id: sessaoId || null,
        mensagem: msg,
        rota_atual: location.pathname,
      }, auth());
      if (!sessaoId) setSessaoId(r.data.sessao_id);
      setMensagens((m) => [...m, { id: `srv-${Date.now()}`, role: 'assistant', content: r.data.resposta }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Maya está indisponível no momento');
      setMensagens((m) => [...m, { id: `err-${Date.now()}`, role: 'assistant', content: '⚠️ Desculpe, tive um problema agora. Pode tentar novamente?' }]);
    } finally { setEnviando(false); }
  }, [input, enviando, sessaoId, location.pathname]);

  const novaConversa = () => {
    setSessaoId(null);
    setMensagens([]);
    setView('chat');
  };

  const carregarSessoes = async () => {
    setCarregandoHist(true);
    try { const r = await axios.get(`${API}/api/assistant/sessoes`, auth()); setSessoes(r.data || []); }
    catch { toast.error('Erro ao carregar histórico'); }
    finally { setCarregandoHist(false); }
  };

  const abrirSessao = async (id) => {
    try {
      const r = await axios.get(`${API}/api/assistant/sessoes/${id}/mensagens`, auth());
      setSessaoId(id);
      setMensagens(r.data.mensagens || []);
      setView('chat');
    } catch { toast.error('Erro'); }
  };

  const deletarSessao = async (id, e) => {
    e?.stopPropagation();
    const ok = await confirm({
      title: 'Excluir conversa?',
      description: 'Esta ação é permanente e remove o histórico desta conversa com a Maya.',
      confirmLabel: 'Excluir',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/assistant/sessoes/${id}`, auth());
      if (id === sessaoId) novaConversa();
      carregarSessoes();
    } catch { toast.error('Erro'); }
  };

  // Não renderiza se usuário não está logado
  if (!isAuthed) return null;

  return (
    <>
      {/* Botão Flutuante */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[60] group"
          data-testid="maya-fab"
          aria-label="Abrir Maya, assistente IA"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-indigo-400 blur-md opacity-60 group-hover:opacity-90 transition-opacity" />
            <div className="relative group-hover:scale-110 transition-transform shadow-2xl rounded-full">
              <MayaAvatarReactive
                variant="happy"
                size="fab"
                pulse
                showStatusDot
                data-testid="maya-fab-avatar"
              />
            </div>
          </div>
          <span className="absolute -top-8 right-1/2 translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded shadow opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Falar com a Maya
          </span>
        </button>
      )}

      {/* Painel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-[60] w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col border border-slate-200 overflow-hidden"
          data-testid="maya-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white flex items-center gap-3">
            {view === 'history' ? (
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 -ml-2"
                onClick={() => setView('chat')}><ArrowLeft className="h-4 w-4" /></Button>
            ) : <MayaAvatar size={36} />}
            <div className="flex-1">
              <p className="font-semibold text-sm">{view === 'history' ? 'Histórico de Conversas' : 'Maya'}</p>
              {view !== 'history' && <p className="text-[10px] opacity-80">Assistente IA do GridProfile</p>}
            </div>
            {view === 'chat' && (
              <>
                <button onClick={novaConversa} className="text-white/90 hover:text-white p-1" title="Nova conversa" data-testid="maya-new-btn">
                  <PlusCircle className="h-4 w-4" />
                </button>
                <button onClick={() => { setView('history'); carregarSessoes(); }} className="text-white/90 hover:text-white p-1" title="Histórico" data-testid="maya-history-btn">
                  <History className="h-4 w-4" />
                </button>
              </>
            )}
            <button onClick={() => setOpen(false)} className="text-white/90 hover:text-white p-1" title="Fechar" data-testid="maya-close-btn">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Corpo */}
          {view === 'chat' ? (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 bg-slate-50">
                {mensagens.length === 0 && (
                  <div className="text-center py-6">
                    <MayaAvatar size={56} />
                    <h3 className="font-semibold mt-3 text-slate-800">Olá! Sou a Maya 👋</h3>
                    <p className="text-xs text-slate-500 mt-1 mb-4 px-4">Especialista no GridProfile. Posso te orientar em qualquer função do sistema.</p>
                    {sugestoes.length > 0 && (
                      <div className="space-y-1.5 px-2">
                        <p className="text-[10px] uppercase text-slate-400 mb-2 tracking-wide">Tente perguntar</p>
                        {sugestoes.slice(0, 4).map((s, i) => (
                          <button key={i} onClick={() => enviar(s)} className="block w-full text-left text-xs bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 rounded-lg px-3 py-2 transition-colors" data-testid={`maya-suggest-${i}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {mensagens.map((m) => <MessageBubble key={m.id} msg={m} onExecutar={executarAcao} />)}
                {enviando && (
                  <div className="flex gap-2 mb-3">
                    <MayaAvatar size={32} animated />
                    <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <form onSubmit={(e) => { e.preventDefault(); enviar(); }} className="border-t bg-white px-3 py-2 flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Pergunte algo à Maya..."
                  className="flex-1 text-sm"
                  disabled={enviando}
                  data-testid="maya-input"
                  maxLength={4000}
                />
                <Button type="submit" size="sm" disabled={!input.trim() || enviando}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="maya-send-btn">
                  {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </>
          ) : (
            // Histórico
            <div className="flex-1 overflow-y-auto px-3 py-2 bg-slate-50">
              {carregandoHist ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
              ) : sessoes.length === 0 ? (
                <p className="text-center text-sm text-slate-500 py-8">Você ainda não tem conversas salvas.</p>
              ) : (
                <div className="space-y-1">
                  {sessoes.map((s) => (
                    <div key={s.id}
                      onClick={() => abrirSessao(s.id)}
                      className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-white hover:shadow-sm border ${s.id === sessaoId ? 'bg-white border-indigo-200' : 'border-transparent'}`}
                      data-testid={`maya-sess-${s.id}`}
                    >
                      <MessageCircle className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-800 truncate">{s.titulo}</p>
                        <p className="text-[10px] text-slate-400">{new Date(s.atualizado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</p>
                      </div>
                      <button onClick={(e) => deletarSessao(s.id, e)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-red-500 hover:text-red-700">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MayaChat;
